import {Async, Loaded, isLoaded} from 'Core/models/Async';
import {DEFAULT_INITIAL_PLAN_NAME} from 'Quoting/constants';
import {RetreivedPlan} from 'Quoting/models/DTOs/QuoteForm';
import {createEisTypeObject, getKey, parsePath} from 'Quoting/utils/utilities';
import {List as ImmutableList, Map as ImmutableMap, fromJS} from 'immutable';
import {exclusionsMapping, healthScreeningServicesRiderDetails} from '../../MappingPaths';
import {
  brokenToothDetailsTimePeriodCd,
  defaultMaxClosed,
  defaultMaxOpen,
  dependentChildOfDislocationRider,
  fractureDefaultValues,
  lacerationLengthMaxBenefitNumber,
  reductionDetailsDefaults,
} from './benefitDefaults';

const createPlansObjectWithDefault = (
  plan: any,
  rootId: string,
  quoteParentId: string,
  coverageEffectiveDate: string,
  defaultValues: ImmutableMap<string, any>,
  situsStateCd: string
) => {
  const key = getKey(rootId, quoteParentId);
  const parentId = key.id;

  return {
    exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId, situsStateCd),
    planCd: plan.planCode,
    planGroupsLinks: [],
    _type: 'ACCBenefitPlan',
    planName: plan.name,
    _key: key,
    coverageDefs: plan.coverages
      .filter((coverage: any) => coverage.applicability === 'Mandatory')
      .map(() => createCoverageDef(defaultValues, rootId, parentId, coverageEffectiveDate, situsStateCd)),

    offerStatus: 'offered',
  };
};

const createCoverageDef = (
  defaultValues: ImmutableMap<string, any>,
  planRootId: string,
  planParentId: string,
  coverageEffectiveDate: string,
  situsStateCd: string
) => {
  const key = getKey(planRootId, planParentId);
  const rootId = planRootId;
  const parentId = key.id;

  return {
    _key: key,
    code: defaultValues.get('ACCCoverageDefinition.code'),
    coverageBasisCd: defaultValues.get('ACCCoverageDefinition.coverageBasisCd'),
    benefitAmountSpouse: {
      benefitPct: defaultValues.get('ACCBenefitAmountSpouse.benefitPct'),
      ...createEisTypeObject('ACCBenefitAmountSpouse', rootId, parentId),
    },

    underwritingDetails: {
      underwritingTypeCd: defaultValues.get('ACCUnderwriting.underwritingTypeCd'),
      ...createEisTypeObject('ACCUnderwriting', rootId, parentId),
    },

    terminationAge: defaultValues.get('ACCCoverageDefinition.terminationAge'),

    benefitStructure: createACCBenefitStructure(defaultValues, rootId, parentId, situsStateCd),
    coveredEvents: defaultValues.get('ACCCoverageDefinition.coveredEvents'),
    effDate: coverageEffectiveDate,
    eligibilities: createACCEligibilities(defaultValues, rootId, parentId),

    ratingDetails: {
      rateFormatCd: defaultValues.get('ACCCoverageRating.rateFormatCd'),
      ...createEisTypeObject('ACCCoverageRating', rootId, parentId),
    },
    originalEffDate: coverageEffectiveDate,
    censusTypeCd: defaultValues.get('ACCCoverageDefinition.censusTypeCd'),
    coverageGroupsLinks: [],
    _type: 'ACCCoverageDefinition',
    fundingStructure: {
      ...createEisTypeObject('ACCFundingStructure', rootId, parentId),
      requiredGroupAccessPct: defaultValues.get('ACCFundingStructure.requiredGroupAccessPct'),
      taxabilityCd: defaultValues.get('ACCFundingStructure.taxabilityCd'),
      minParticipantsNum: defaultValues.get('ACCFundingStructure.minParticipantsNum'),
      contributionTypeCd: defaultValues.get('ACCFundingStructure.contributionTypeCd'),
      fundingTypeCd: defaultValues.get('ACCFundingStructure.fundingTypeCd'),
      payrollFrequencyCd: undefined,
    },
    benefitSchedule: createEisTypeObject('ACCBenefitSchedule', rootId, parentId),
    benefitAmountChild: {
      benefitPct: defaultValues.get('ACCBenefitAmountChild.benefitPct'),
      ...createEisTypeObject('ACCBenefitAmountChild', rootId, parentId),
    },
    offerStatus: 'offered',

    issueAge: {
      minIssueAgeChild: defaultValues.get('ACCEligibilityIssueAges.minIssueAgeChild'),
      maxIssueAgeChild: defaultValues.get('ACCEligibilityIssueAges.maxIssueAgeChild'),
      minIssueAgeIndividual: defaultValues.get('ACCEligibilityIssueAges.minIssueAgeIndividual'),
      minIssueAgeSpouse: defaultValues.get('ACCEligibilityIssueAges.minIssueAgeSpouse'),
      minAgeIndividual: defaultValues.get('ACCEligibilityIssueAges.minAgeIndividual'),
      minAgeChildren: defaultValues.get('ACCEligibilityIssueAges.minAgeChildren'),
      maxAgeChildren: defaultValues.get('ACCEligibilityIssueAges.maxAgeChildren'),

      ...createEisTypeObject('ACCEligibilityIssueAges', rootId, parentId),
    },
    whoCoveredCd: defaultValues.get('ACCCoverageDefinition.whoCoveredCd'),
    whoCovered: createWhoCovered(rootId, parentId),
  };
};

const createBenefitPlanExclusions = (
  defaultValues: ImmutableMap<string, any>,
  rootId?: string,
  parentId?: string,
  situsStateCd?: string
) => {
  const exclusionsACC = [
    {
      exclusionEntityName: 'ACCExclAnyActOfWar',
      isExclusion: defaultValues.get('ACCExclAnyActOfWar.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclInfectionCausedByAnAccidentalCutOrWound',
      isExclusion: defaultValues.get('ACCExclInfectionCausedByAnAccidentalCutOrWound.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclHernia',
      isExclusion: defaultValues.get('ACCExclHernia.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclOnTheJobAccident',
      isExclusion: defaultValues.get('ACCExclOnTheJobAccident.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclParticipationInRiot',
      isExclusion: defaultValues.get('ACCExclParticipationInRiot.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclAeronauticsWithException',
      isExclusion: defaultValues.get('ACCExclAeronauticsWithException.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclOperatingVehicleWithExceededAlcoholLevel',
      isExclusion: defaultValues.get('ACCExclOperatingVehicleWithExceededAlcoholLevel.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclMedicalMalpractice',
      isExclusion: defaultValues.get('ACCExclMedicalMalpractice.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclSuicide',
      isExclusion: defaultValues.get('ACCExclSuicide.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclIllegalOccupationOrCommittingAnAssaultOrFelony',
      isExclusion: defaultValues.get('ACCExclIllegalOccupationOrCommittingAnAssaultOrFelony.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclVoluntaryTakingOfAnyPoison',
      isExclusion: defaultValues.get('ACCExclVoluntaryTakingOfAnyPoison.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclElectiveSurgery',
      isExclusion: defaultValues.get('ACCExclElectiveSurgery.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclSelfInflictedInjuryOrAction',
      isExclusion: defaultValues.get('ACCExclSelfInflictedInjuryOrAction.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclOrganizedOrScheduledRaceOrSpeedTest',
      isExclusion: defaultValues.get('ACCExclOrganizedOrScheduledRaceOrSpeedTest.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclVoluntaryTakingOfAnyDrugOrSedative',
      isExclusion: defaultValues.get('ACCExclVoluntaryTakingOfAnyDrugOrSedative.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclPregnancy',
      isExclusion: defaultValues.get('ACCExclPregnancy.isExclusion'),
    },
  ];
  if (situsStateCd === 'MI') {
    exclusionsACC.push({
      exclusionEntityName: 'ACCExclWillfulCriminalActivity',
      isExclusion: defaultValues.get('ACCExclWillfulCriminalActivity.isExclusion'),
    });
  }

  return createExclusions(exclusionsACC, rootId, parentId);
};

const createExclusions = (
  exclusions: {exclusionEntityName: string; isExclusion: boolean}[],
  rootId?: string,
  parentId?: string
) =>
  exclusions.map(({exclusionEntityName, isExclusion}) => ({
    ...createEisTypeObject(exclusionEntityName, rootId, parentId),
    isExclusion: isExclusion,
  }));

const createACCEligibilities = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return [
    {
      waitingPeriodAmount: defaultValues.get('ACCEligibility.waitingPeriodAmount'),
      waitingPeriodModeCd: defaultValues.get('ACCEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('ACCEligibility.minHourlyReq'),
      waitingPeriodDefCd: defaultValues.get('ACCEligibility.waitingPeriodDefCd'),
      eligibilityTypeCd: 'ExistingEmployees',
    },
    {
      waitingPeriodModeCd: defaultValues.get('ACCEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('ACCEligibility.minHourlyReq'),
      eligibilityTypeCd: 'NewEmployees',
    },
  ].map(e => ({...e, ...createEisTypeObject('ACCEligibility', rootId, parentId)}));
};

// Helper function for recalculating benefits based on units for dislocation rider
export const getCalculatedBenefitAmounts = (units: number) => {
  const calculatedMaxClosed = units === 1 ? defaultMaxClosed : Number(units) * defaultMaxClosed;
  const calculatedMaxOpen = units === 1 ? defaultMaxOpen : Number(units) * defaultMaxOpen;

  return {
    maximumClosedReductionBenefitAmount: getMoneyObject(calculatedMaxClosed),
    maximumOpenReductionBenefitAmount: getMoneyObject(calculatedMaxOpen),
  };
};

export const getMoneyObject = (value: number) => ({_type: 'Money', amount: value, currency: 'USD'});

const createWhoCovered = (rootId: string, parentId: string) =>
  ['Individual', 'IndividualAndSpouse', 'IndividualAndChildren', 'Family'].map(tierCd => ({
    ...createEisTypeObject('ACCTier', rootId, parentId),
    tierCd: tierCd,
  }));

export const createACCBenefitStructure = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  situsStateCd: string
) => {
  const stepDownIntensiveCareUnitConfinementDetails = {
    incurralPeriod: defaultValues.get('ACCStepDownIntensiveCareUnitConfinementBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCStepDownIntensiveCareUnitConfinementBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCStepDownIntensiveCareUnitConfinementBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCStepDownIntensiveCareUnitConfinementBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCStepDownIntensiveCareUnitConfinementBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCStepDownIntensiveCareUnitConfinementBenefit', rootId, parentId),
  };

  const rehabilitationFacilityDetails = {
    incurralPeriod: defaultValues.get('ACCRehabilitationFacilityBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCRehabilitationFacilityBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCRehabilitationFacilityBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCRehabilitationFacilityBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCRehabilitationFacilityBenefit.timePeriodCd'),
    maxBenefitNumber2: defaultValues.get('ACCRehabilitationFacilityBenefit.maxBenefitNumber2'),
    timePeriod2Cd: defaultValues.get('ACCRehabilitationFacilityBenefit.timePeriod2Cd'),
    ...createEisTypeObject('ACCRehabilitationFacilityBenefit', rootId, parentId),
  };

  const punctureWoundDetails = {
    incurralPeriod: defaultValues.get('ACCPunctureWoundBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCPunctureWoundBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCPunctureWoundBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCPunctureWoundBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCPunctureWoundBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCPunctureWoundBenefit', rootId, parentId),
  };

  const gunshotWoundDetails = {
    incurralPeriod: defaultValues.get('ACCGunshotWoundBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCGunshotWoundBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCGunshotWoundBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCGunshotWoundBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCGunshotWoundBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCGunshotWoundBenefit', rootId, parentId),
  };

  const followUpTreatmentDetails = {
    units: defaultValues.get('ACCFollowUpTreatmentBenefit.units'),
    incurralPeriod: defaultValues.get('ACCFollowUpTreatmentBenefit.incurralPeriod'),
    isBenefitAmountCustomize: defaultValues.get('ACCFollowUpTreatmentBenefit.isBenefitAmountCustomize'),
    benefitAmount: defaultValues.get('ACCFollowUpTreatmentBenefit.standartBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCFollowUpTreatmentBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCFollowUpTreatmentBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCFollowUpTreatmentBenefit', rootId, parentId),
  };

  const xRayDetails = {
    units: defaultValues.get('ACCXRayBenefit.units'),
    incurralPeriod: defaultValues.get('ACCXRayBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCXRayBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCXRayBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCXRayBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCXRayBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCXRayBenefit', rootId, parentId),
  };

  const emergencyRoomServicesDetails = {
    unitsOfEmergencyRoomServices: defaultValues.get('ACCEmergencyRoomServicesBenefit.unitsOfEmergencyRoomServices'),
    incurralPeriod: defaultValues.get('ACCEmergencyRoomServicesBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCEmergencyRoomServicesBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCEmergencyRoomServicesBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCEmergencyRoomServicesBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCEmergencyRoomServicesBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCEmergencyRoomServicesBenefit', rootId, parentId),
  };
  const painManagementDetails = {
    incurralPeriod: defaultValues.get('ACCPainManagementBenefit.incurralPeriod'),
    benefitAmount: defaultValues.get('ACCPainManagementBenefit.standardBenefitAmount'),
    epiduralInjection: defaultValues.get('ACCPainManagementBenefit.epiduralInjection'),
    nerveAblationProcedure: defaultValues.get('ACCPainManagementBenefit.nerveAblationProcedure'),
    maxBenefitNumber: defaultValues.get('ACCPainManagementBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCPainManagementBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCPainManagementBenefit', rootId, parentId),
  };

  const painManagementCorticosteroidInjectionBenefit = {
    incurralPeriod: defaultValues.get('ACCPainManagementCorticosteroidInjectionBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCPainManagementCorticosteroidInjectionBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCPainManagementCorticosteroidInjectionBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCPainManagementCorticosteroidInjectionBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCPainManagementCorticosteroidInjectionBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCPainManagementCorticosteroidInjectionBenefit', rootId, parentId),
  };

  const automobileAccidentalDeathDetails = {
    incurralPeriod: defaultValues.get('ACCAutomobileAccidentalDeathBenefit.incurralPeriod'),
    benefitAmount: defaultValues.get('ACCAutomobileAccidentalDeathBenefit.standardBenefitAmount'),
    ...createEisTypeObject('ACCAutomobileAccidentalDeathBenefit', rootId, parentId),
  };

  const dislocationFractureRiderDetails = {
    scheduleOfDislocationRider: defaultValues.get('ACCDislocationFractureRiderDetails.scheduleOfDislocationRider'),
    dependentChildOfDislocationRider: dependentChildOfDislocationRider,
    timesMaximumClosedBenefit: defaultValues.get('ACCDislocationFractureRiderDetails.timesMaximumClosedBenefit'),
    timePeroidMaximumClosedBenefit: defaultValues.get(
      'ACCDislocationFractureRiderDetails.timePeroidMaximumClosedBenefit'
    ),
    timesMaximumOpenBenefit: defaultValues.get('ACCDislocationFractureRiderDetails.timesMaximumOpenBenefit'),
    timePeroidMaximumOpenBenefit: defaultValues.get('ACCDislocationFractureRiderDetails.timePeroidMaximumOpenBenefit'),
    ...getCalculatedBenefitAmounts(defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1),
    ...createEisTypeObject('ACCDislocationFractureRiderDetails', rootId, parentId),
  };

  const dislocationDetails = {
    incurralPeriod: defaultValues.get('ACCDislocationBenefit.incurralPeriod'),
    partialDislocation: defaultValues.get('ACCDislocationBenefit.partialDislocation'),
    reductionAmountDetails: [
      // Open Reduction Amount
      {
        hipAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.hipStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        kneeNoPatellaAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        footBonesAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.footBonesStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        ankleAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.ankleStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        wristAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.wristStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        elbowAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.elbowStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        shoulderGlenohumeralAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        handBonesAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.handBonesStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        collarboneAcromioclavicularAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        twoOrMoreFingersAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        twoOrMoreToesAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        oneFingerOrToeAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        kneecapAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.kneecapStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        lowerJawAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.lowerJawStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        otherDislocationAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.otherDislocationStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        dislocationReductionAmountTypeCd: 'Open',
        ...createEisTypeObject('ACCDislocationReductionAmt', rootId, parentId),
      },
      // Closed Reduction Amount
      {
        hipAmount: getMoneyObject(
          reductionDetailsDefaults.hipStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        kneeNoPatellaAmount: getMoneyObject(
          reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        footBonesAmount: getMoneyObject(
          reductionDetailsDefaults.footBonesStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        ankleAmount: getMoneyObject(
          reductionDetailsDefaults.ankleStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        wristAmount: getMoneyObject(
          reductionDetailsDefaults.wristStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        elbowAmount: getMoneyObject(
          reductionDetailsDefaults.elbowStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        shoulderGlenohumeralAmount: getMoneyObject(
          reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        handBonesAmount: getMoneyObject(
          reductionDetailsDefaults.handBonesStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        collarboneAcromioclavicularAmount: getMoneyObject(
          reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        twoOrMoreFingersAmount: getMoneyObject(
          reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        twoOrMoreToesAmount: getMoneyObject(
          reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        oneFingerOrToeAmount: getMoneyObject(
          reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        kneecapAmount: getMoneyObject(
          reductionDetailsDefaults.kneecapStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        lowerJawAmount: getMoneyObject(
          reductionDetailsDefaults.lowerJawStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        otherDislocationAmount: getMoneyObject(
          reductionDetailsDefaults.otherDislocationStandardBenefitAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
        ),
        dislocationReductionAmountTypeCd: 'Closed',
        ...createEisTypeObject('ACCDislocationReductionAmt', rootId, parentId),
      },
    ],
    ...createEisTypeObject('ACCDislocationBenefit', rootId, parentId),
  };

  const fractureDetails = {
    incurralPeriod: defaultValues.get('ACCFractureBenefit.incurralPeriod'),
    reductionAmountDetails: [
      {
        hipAmount: getMoneyObject(
          3 *
            fractureDefaultValues.hipThighFemurFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        skullFractureNonDepressedNoFaceNoseBonesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.skullFractureNonDepressedAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount: getMoneyObject(
          3 *
            fractureDefaultValues.pelvisFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        upperArmBetwElbowAndShoulderHumerusAmount: getMoneyObject(
          3 *
            fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        shoulderBladeScapulaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.shoulderBladeFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        legTibiaFibulaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.legFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        ankleAmount: getMoneyObject(
          3 *
            fractureDefaultValues.ankleFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        kneecapPatellaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.kneecapFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        collarboneClavicleSternumAmount: getMoneyObject(
          3 *
            fractureDefaultValues.collarBoneFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        forearmRadiusUlnaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.forearmFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        footOrHeelNoToesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.footFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        handNoFingersAmount: getMoneyObject(
          3 *
            fractureDefaultValues.handOrWristFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        lowerJawMandibleNoAlveolarProcessAmount: getMoneyObject(
          3 *
            fractureDefaultValues.lowerJawFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        twoOrMoreRibsFingersOrToesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        faceOrNoseBonesNoMandibleOrMaxillaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.bonesOfFaceFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        noseAmount: getMoneyObject(
          3 *
            fractureDefaultValues.noseFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        oneRibFingerOrToeAmount: getMoneyObject(
          3 *
            fractureDefaultValues.oneRibFingerToeFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        coccyxAmount: getMoneyObject(
          3 *
            fractureDefaultValues.coccyxFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        skullFractureDepressedNoFaceNoseBonesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.skullFractureDepressedAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        vertebraeBodyOfNoVertebralProcessesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.vertebralBodyFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        vertebralProcessesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.vertebralProcessFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        upperJawMaxillaNoAlveolarProcessAmount: getMoneyObject(
          3 *
            fractureDefaultValues.upperJawFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        sternumAmount: getMoneyObject(
          3 *
            fractureDefaultValues.sternumFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        anyOtherFractureAmount: getMoneyObject(
          3 *
            fractureDefaultValues.anyOtherFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        fractureReductionAmountTypeCd: 'Open',
        ...createEisTypeObject('ACCFractureReductionAmt', rootId, parentId),
      },
      {
        hipAmount: getMoneyObject(
          fractureDefaultValues.hipThighFemurFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        skullFractureNonDepressedNoFaceNoseBonesAmount: getMoneyObject(
          fractureDefaultValues.skullFractureNonDepressedAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount: getMoneyObject(
          fractureDefaultValues.pelvisFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        upperArmBetwElbowAndShoulderHumerusAmount: getMoneyObject(
          fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        shoulderBladeScapulaAmount: getMoneyObject(
          fractureDefaultValues.shoulderBladeFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        legTibiaFibulaAmount: getMoneyObject(
          fractureDefaultValues.legFractureAmount * (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        ankleAmount: getMoneyObject(
          fractureDefaultValues.ankleFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        kneecapPatellaAmount: getMoneyObject(
          fractureDefaultValues.kneecapFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        collarboneClavicleSternumAmount: getMoneyObject(
          fractureDefaultValues.collarBoneFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        forearmRadiusUlnaAmount: getMoneyObject(
          fractureDefaultValues.forearmFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        footOrHeelNoToesAmount: getMoneyObject(
          fractureDefaultValues.footFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        handNoFingersAmount: getMoneyObject(
          fractureDefaultValues.handOrWristFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        lowerJawMandibleNoAlveolarProcessAmount: getMoneyObject(
          fractureDefaultValues.lowerJawFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        twoOrMoreRibsFingersOrToesAmount: getMoneyObject(
          fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        faceOrNoseBonesNoMandibleOrMaxillaAmount: getMoneyObject(
          fractureDefaultValues.bonesOfFaceFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        noseAmount: getMoneyObject(
          fractureDefaultValues.noseFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        oneRibFingerOrToeAmount: getMoneyObject(
          fractureDefaultValues.oneRibFingerToeFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        coccyxAmount: getMoneyObject(
          fractureDefaultValues.coccyxFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        skullFractureDepressedNoFaceNoseBonesAmount: getMoneyObject(
          fractureDefaultValues.skullFractureDepressedAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        vertebraeBodyOfNoVertebralProcessesAmount: getMoneyObject(
          fractureDefaultValues.vertebralBodyFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        vertebralProcessesAmount: getMoneyObject(
          fractureDefaultValues.vertebralProcessFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        upperJawMaxillaNoAlveolarProcessAmount: getMoneyObject(
          fractureDefaultValues.upperJawFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        sternumAmount: getMoneyObject(
          fractureDefaultValues.sternumFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        anyOtherFractureAmount: getMoneyObject(
          fractureDefaultValues.anyOtherFractureAmount *
            (defaultValues.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
        ),
        fractureReductionAmountTypeCd: 'Closed',
        ...createEisTypeObject('ACCFractureReductionAmt', rootId, parentId),
      },
    ],
    chipFracture: defaultValues.get('ACCFractureBenefit.chipFracture'),
    stressFractures: defaultValues.get('ACCFractureBenefit.stressFractures'),
    ...createEisTypeObject('ACCFractureBenefit', rootId, parentId),
  };

  return {
    _key: getKey(rootId, parentId),
    unitsOfAccidentFacilityCare: defaultValues.get('ACCBenefitStructure.unitsOfAccidentFacilityCare'),
    isHospitalAdmissionApplied: defaultValues.get('ACCBenefitStructure.isHospitalAdmissionApplied'),
    isHospitalConfinementApplied: defaultValues.get('ACCBenefitStructure.isHospitalConfinementApplied'),
    isIntensiveCareUnitConfinementApplied: defaultValues.get(
      'ACCBenefitStructure.isIntensiveCareUnitConfinementApplied'
    ),
    isStepDownIntensiveCareUnitConfinementApplied: defaultValues.get(
      'ACCBenefitStructure.isStepDownIntensiveCareUnitConfinementApplied'
    ),
    isRehabilitationFacilityApplied: defaultValues.get('ACCBenefitStructure.isRehabilitationFacilityApplied'),
    isFollowUpTreatmentApplied: defaultValues.get('ACCBenefitStructure.isFollowUpTreatmentApplied'),
    isSubstanceAbuseConfinementApplied: false,

    offerStatus: 'offered',

    hospitalAdmissionDetails: {
      incurralPeriod: defaultValues.get('ACCHospitalAdmissionBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCHospitalAdmissionBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCHospitalAdmissionBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCHospitalAdmissionBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCHospitalAdmissionBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCHospitalAdmissionBenefit', rootId, parentId),
    },

    hospitalConfinementDetails: {
      incurralPeriod: defaultValues.get('ACCHospitalConfinementBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCHospitalConfinementBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCHospitalConfinementBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCHospitalConfinementBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCHospitalConfinementBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCHospitalConfinementBenefit', rootId, parentId),
    },

    intensiveCareUnitConfinementDetails: {
      incurralPeriod: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCIntensiveCareUnitConfinementBenefit', rootId, parentId),
    },

    stepDownIntensiveCareUnitConfinementDetails: stepDownIntensiveCareUnitConfinementDetails,
    rehabilitationFacilityDetails: rehabilitationFacilityDetails,
    //Accident injuries

    unitsOfAccidentInjuries: Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries')) || 1,
    isBrainInjuryDiagnosisApplied: defaultValues.get('ACCBenefitStructure.isBrainInjuryDiagnosisApplied'),
    isBrokenToothApplied: defaultValues.get('ACCBenefitStructure.isBrokenToothApplied'),
    isBurnApplied: defaultValues.get('ACCBenefitStructure.isBurnApplied'),
    isBurnSkinGraftApplied: defaultValues.get('ACCBenefitStructure.isBurnSkinGraftApplied'),
    isComaApplied: defaultValues.get('ACCBenefitStructure.isComaApplied'),
    isLacerationApplied: defaultValues.get('ACCBenefitStructure.isLacerationApplied'),
    isParalysisApplied: defaultValues.get('ACCBenefitStructure.isParalysisApplied'),
    isPunctureWoundApplied:
      Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries')) === 0
        ? false
        : defaultValues.get('ACCBenefitStructure.isPunctureWoundApplied'),
    isGunshotWoundApplied:
      Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries')) === 0
        ? false
        : defaultValues.get('ACCBenefitStructure.isGunshotWoundApplied'),

    //Brain Injuries Section

    brainInjuryDiagnosisDetails: {
      incurralPeriod: defaultValues.get('ACCBrainInjuryDiagnosisDetails.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCBrainInjuryDiagnosisDetails.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCBrainInjuryDiagnosisDetails.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCBrainInjuryDiagnosisDetails.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBrainInjuryDiagnosisDetails.timePeriodCd'),
      ...createEisTypeObject('ACCBrainInjuryDiagnosisDetails', rootId, parentId),
    },

    //Broken Tooth Section
    brokenToothDetails: {
      incurralPeriod: defaultValues.get('ACCBrokenToothBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCBrokenToothBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCBrokenToothBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCBrokenToothBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBrokenToothBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBrokenToothBenefit', rootId, parentId),
    },

    //Burn Details

    burnDetails: {
      incurralPeriod: defaultValues.get('ACCBurnBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCBurnBenefit.customizeBenefitAmount'),

      burnDegreeDetails: [
        //second degree burn
        {
          burnSize1Amount: getMoneyObject(
            2000 * (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) * 0.0125
          ),
          burnSize2Amount: getMoneyObject(
            2000 * (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) * 0.025
          ),
          burnSize3Amount: getMoneyObject(
            2000 * (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) * 0.125
          ),
          burnDegreeTypeCd: 'Second',
          ...createEisTypeObject('ACCBurnDegree', rootId, parentId),
        },
        //third degree burn
        {
          burnSize1Amount: getMoneyObject(
            2000 * (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) * 0.05
          ),
          burnSize2Amount: getMoneyObject(
            2000 * (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) * 0.25
          ),
          burnSize3Amount: getMoneyObject(
            2000 * (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1)
          ),
          burnDegreeTypeCd: 'Third',
          ...createEisTypeObject('ACCBurnDegree', rootId, parentId),
        },
      ],
      maxBenefitNumber: defaultValues.get('ACCBurnBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBurnBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBurnBenefit', rootId, parentId),
    },

    //ACCComaBenefit
    comaDetails: {
      incurralPeriod: defaultValues.get('ACCComaBenefit.incurralPeriod'),
      waitingPeriod: defaultValues.get('ACCComaBenefit.waitingPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCComaBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCComaBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCComaBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCComaBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCComaBenefit', rootId, parentId),
    },

    //ACCBurnSkinGraftBenefit

    burnSkinGraftDetails: {
      incurralPeriod: defaultValues.get('ACCBurnSkinGraftBenefit.incurralPeriod'),
      burnsBenefitPct: defaultValues.get('ACCBurnSkinGraftBenefit.burnsBenefitPct'),
      maxBenefitNumber: defaultValues.get('ACCBurnSkinGraftBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBurnSkinGraftBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBurnSkinGraftBenefit', rootId, parentId),
    },

    //ACCLacerationBenefit
    lacerationDetails: {
      incurralPeriod: defaultValues.get('ACCLacerationBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCLacerationBenefit.customizeBenefitAmount'),

      lacerationLength3Amount: getMoneyObject(
        (defaultValues.get('ACCLacerationBenefit.standardBenefitAmount')?.get('amount') || 0) *
          (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1)
      ),
      lacerationLength1Amount: getMoneyObject(
        (defaultValues.get('ACCLacerationBenefit.standardBenefitAmount')?.get('amount') || 0) *
          (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) *
          0.2
      ),
      lacerationLength2Amount: getMoneyObject(
        (defaultValues.get('ACCLacerationBenefit.standardBenefitAmount')?.get('amount') || 0) *
          (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) *
          0.5
      ),

      withoutRepairAmount: getMoneyObject(
        (defaultValues.get('ACCLacerationBenefit.standardBenefitAmount')?.get('amount') || 0) *
          (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) *
          0.2
      ),
      maxBenefitNumber: lacerationLengthMaxBenefitNumber,
      timePeriodCd: brokenToothDetailsTimePeriodCd,
      ...createEisTypeObject('ACCLacerationBenefit', rootId, parentId),
    },

    //ACCParalysisBenefit

    paralysisDetails: {
      incurralPeriod: defaultValues.get('ACCParalysisBenefit.incurralPeriod'),
      minimumDuration: defaultValues.get('ACCParalysisBenefit.minimumDuration'),
      customizeBenefitAmount: defaultValues.get('ACCParalysisBenefit.customizeBenefitAmount'),
      threeOrFourLimbsAmount: getMoneyObject(
        defaultValues.get('ACCParalysisBenefit.standardBenefitAmount')?.get('amount') ||
          0 * defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries')
      ),
      oneOrTwoLimbsAmount: getMoneyObject(
        (defaultValues.get('ACCParalysisBenefit.standardBenefitAmount')?.get('amount') || 0) *
          (defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries') || 1) *
          0.5
      ),
      maxBenefitNumber: defaultValues.get('ACCParalysisBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCParalysisBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCParalysisBenefit', rootId, parentId),
    },

    //ACCPunctureWoundBenefit

    punctureWoundDetails: punctureWoundDetails,

    gunshotWoundDetails: gunshotWoundDetails,

    followUpTreatmentDetails: followUpTreatmentDetails,

    unitsOfAccAmbulanceUrgentCare: defaultValues.get('ACCBenefitStructure.unitsOfAccAmbulanceUrgentCare'),
    isAmbulanceAirApplied: defaultValues.get('ACCBenefitStructure.isAmbulanceAirApplied'),
    isAmbulanceGroundOrWaterApplied: defaultValues.get('ACCBenefitStructure.isAmbulanceGroundOrWaterApplied'),
    isUrgentCareApplied: defaultValues.get('ACCBenefitStructure.isUrgentCareApplied'),
    isAccidentPhysicianTreatmentApplied: defaultValues.get('ACCBenefitStructure.isAccidentPhysicianTreatmentApplied'),

    ambulanceUrgentCareBenefit: {
      incurralPeriod: defaultValues.get('ACCAmbulanceUrgentCareBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCAmbulanceUrgentCareBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCAmbulanceUrgentCareBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCAmbulanceUrgentCareBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAmbulanceUrgentCareBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAmbulanceUrgentCareBenefit', rootId, parentId),
    },

    //ACCAmbulanceAirBenefit
    ambulanceAirDetails: {
      incurralPeriod: defaultValues.get('ACCAmbulanceAirBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCAmbulanceAirBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCAmbulanceAirBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCAmbulanceAirBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAmbulanceAirBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAmbulanceAirBenefit', rootId, parentId),
    },
    //ACCAmbulanceGroundOrWaterBenefit
    ambulanceGroundOrWaterDetails: {
      incurralPeriod: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAmbulanceGroundOrWaterBenefit', rootId, parentId),
    },

    //ACCUrgentCareBenefit

    urgentCareDetails: {
      incurralPeriod: defaultValues.get('ACCUrgentCareBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCUrgentCareBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCUrgentCareBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCUrgentCareBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCUrgentCareBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCUrgentCareBenefit', rootId, parentId),
    },

    isXRayApplied: defaultValues.get('ACCBenefitStructure.isXRayApplied'),

    xRayDetails: xRayDetails,

    //ACCEmergencyRoomServicesBenefit

    isEmergencyRoomServicesApplied: defaultValues.get('ACCBenefitStructure.isEmergencyRoomServicesApplied'),
    unitsOfAccAccidentSurgeries: Number(defaultValues.get('ACCBenefitStructure.unitsOfAccAccidentSurgeries')) || 1,
    emergencyRoomServicesDetails: emergencyRoomServicesDetails,

    //ACCMiscellaneousOutpatientSurgeryBenefit
    isMiscellaneousOutpatientSurgeryApplied: defaultValues.get(
      'ACCBenefitStructure.isMiscellaneousOutpatientSurgeryApplied'
    ),
    miscellaneousOutpatientSurgeryDetails: {
      incurralPeriod: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMiscellaneousOutpatientSurgeryBenefit', rootId, parentId),
    },
    //ACCOpenAbdominalOrThoracicSurgeryBenefit
    isOpenAbdominalOrThoracicSurgeryApplied: defaultValues.get(
      'ACCBenefitStructure.isOpenAbdominalOrThoracicSurgeryApplied'
    ),
    openAbdominalOrThoracicSurgeryDetails: {
      incurralPeriod: defaultValues.get('ACCOpenAbdominalOrThoracicSurgeryBenefit.incurralPeriod'),
      benefitAmount: getMoneyObject(1000),
      maxBenefitNumber: defaultValues.get('ACCOpenAbdominalOrThoracicSurgeryBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCOpenAbdominalOrThoracicSurgeryBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCOpenAbdominalOrThoracicSurgeryBenefit', rootId, parentId),
    },

    //ACCTendonLigamentRotatorCuffBenefit
    isTendonLigamentRotatorCuffApplied: defaultValues.get('ACCBenefitStructure.isTendonLigamentRotatorCuffApplied'),
    tendonLigamentRotatorCuffDetails: {
      incurralPeriod: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.incurralPeriod'),
      maxBenefitNumber: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.timePeriodCd'),
      withoutRepairAmount: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.withoutRepairStandardBenefitAmount'),
      withRepairAmount: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.withRepairStandardBenefitAmount'),
      ...createEisTypeObject('ACCTendonLigamentRotatorCuffBenefit', rootId, parentId),
    },

    //ACCGeneralAnesthesiaBenefit
    isGeneralAnesthesiaApplied: defaultValues.get('ACCBenefitStructure.isGeneralAnesthesiaApplied'),
    generalAnesthesiaDetails: {
      incurralPeriod: defaultValues.get('ACCGeneralAnesthesiaBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCGeneralAnesthesiaBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCGeneralAnesthesiaBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCGeneralAnesthesiaBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCGeneralAnesthesiaBenefit', rootId, parentId),
    },
    //ACCRupturedDiscBenefit
    isRupturedDiscApplied: defaultValues.get('ACCBenefitStructure.isRupturedDiscApplied'),
    rupturedDiscDetails: {
      incurralPeriod: defaultValues.get('ACCRupturedDiscBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCRupturedDiscBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCRupturedDiscBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCRupturedDiscBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCRupturedDiscBenefit', rootId, parentId),
    },
    //ACCBloodPlasmaPlateletsBenefit
    isBloodPlasmaPlateletsApplied: defaultValues.get('ACCBenefitStructure.isBloodPlasmaPlateletsApplied'),
    bloodPlasmaPlateletsDetails: {
      incurralPeriod: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBloodPlasmaPlateletsBenefit', rootId, parentId),
    },

    //ACCEyeInjuryBenefit
    isEyeInjuryApplied: defaultValues.get('ACCBenefitStructure.isEyeInjuryApplied'),
    eyeInjuryDetails: {
      incurralPeriod: defaultValues.get('ACCEyeInjuryBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCEyeInjuryBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCEyeInjuryBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCEyeInjuryBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCEyeInjuryBenefit', rootId, parentId),
    },
    //pain management
    unitsOfDiagnosticPainManagement:
      Number(defaultValues.get('ACCBenefitStructure.unitsOfDiagnosticPainManagement')) || 1,
    isMajorDiagnosticExamApplied: defaultValues.get('ACCBenefitStructure.isMajorDiagnosticExamApplied'),
    majorDiagnosticBenefitAmt: getMoneyObject(
      defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount')?.get('amount')
    ),
    majorDiagnosticExamDetails: {
      incurralPeriod: defaultValues.get('ACCMajorDiagnosticExamBenefit.incurralPeriod'),
      cATCustomizeBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.cATCustomizeBenefitAmount'),
      cATBenefitAmount: getMoneyObject(
        defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfDiagnosticPainManagement')) || 1)
      ),
      eEGCustomizeBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.eEGCustomizeBenefitAmount'),
      eEGBenefitAmount: getMoneyObject(
        defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfDiagnosticPainManagement')) || 1)
      ),
      mRICustomizeBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.mRICustomizeBenefitAmount'),
      mRIBenefitAmount: getMoneyObject(
        defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfDiagnosticPainManagement')) || 1)
      ),
      pETCustomizeBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.pETCustomizeBenefitAmount'),
      pETBenefitAmount: getMoneyObject(
        defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfDiagnosticPainManagement')) || 1)
      ),
      ultrasoundCustomizeBenefitAmount: defaultValues.get(
        'ACCMajorDiagnosticExamBenefit.ultrasoundCustomizeBenefitAmount'
      ),
      ultrasoundBenefitAmount: getMoneyObject(
        defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfDiagnosticPainManagement')) || 1)
      ),
      maxBenefitNumber: defaultValues.get('ACCMajorDiagnosticExamBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMajorDiagnosticExamBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMajorDiagnosticExamBenefit', rootId, parentId),
    },

    isPainManagementApplied: defaultValues.get('ACCBenefitStructure.isPainManagementApplied'),

    //ACCPainManagementBenefit
    painManagementDetails: painManagementDetails,
    isPainManagementCorticosteroidInjectionApplied: defaultValues.get(
      'ACCBenefitStructure.isPainManagementCorticosteroidInjectionApplied'
    ),
    //ACCPainManagementCorticosteroidInjectionBenefit
    painManagementCorticosteroidInjectionBenefit: painManagementCorticosteroidInjectionBenefit,

    isOutpatientPhysicianTreatmentsApplied: defaultValues.get(
      'ACCBenefitStructure.isOutpatientPhysicianTreatmentsApplied'
    ),
    isHealthScreeningServicesRiderApplied: defaultValues.get(
      'ACCBenefitStructure.isHealthScreeningServicesRiderApplied'
    ),
    outpatientPhysicianTreatmentsDetails: {
      typeOfRider: 'HSA', //defaulting to HSA
      //defaultValues.get('ACCOutpatientPhysicianTreatments.typeOfRider'),
      units: 2, // defaultValues.get('ACCOutpatientPhysicianTreatments.units'),
      maxBenefitNumber: defaultValues.get('ACCOutpatientPhysicianTreatments.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCOutpatientPhysicianTreatments.timePeriodCd'),
      benefitAmount: defaultValues.get('ACCOutpatientPhysicianTreatments.benefitAmount'),
      ...createEisTypeObject('ACCOutpatientPhysicianTreatments', rootId, parentId),
    },

    isOrganizedSportsActivityInjuryRiderApplied: defaultValues.get(
      'ACCBenefitStructure.isOrganizedSportsActivityInjuryRiderApplied'
    ),

    organizedSportsActivityInjuryRiderDetails: {
      benefitPct: defaultValues.get('ACCOrganizedSportsActivityInjuryRider.benefitPct'),
      isAccidentFacilityCareApplied: defaultValues.get(
        'ACCOrganizedSportsActivityInjuryRider.isAccidentFacilityCareApplied'
      ),
      isAccidentInjuriesApplied: defaultValues.get('ACCOrganizedSportsActivityInjuryRider.isAccidentInjuriesApplied'),
      isAccidentTreatmentUrgentCareApplied: defaultValues.get(
        'ACCOrganizedSportsActivityInjuryRider.isAccidentTreatmentUrgentCareApplied'
      ),
      isTreatmentSupportRecoveryApplied: defaultValues.get(
        'ACCOrganizedSportsActivityInjuryRider.isTreatmentSupportRecoveryApplied'
      ),
      isDislocationFractureRiderApplied: defaultValues.get(
        'ACCOrganizedSportsActivityInjuryRider.isDislocationFractureRiderApplied'
      ),
      ...createEisTypeObject('ACCOrganizedSportsActivityInjuryRider', rootId, parentId),
    },

    healthScreeningServicesRiderDetails: createACCGAI7HealthScreeningServicesRiderDetails(
      defaultValues,
      rootId,
      parentId,
      situsStateCd
    ),

    isDislocationAndFractureRiderBenefitApplied: defaultValues.get(
      'ACCBenefitStructure.isDislocationAndFractureRiderBenefitApplied'
    ),
    unitsOfDislocationRider: Number(defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider')) || 1, //remove later from PNRs
    dislocationFractureRiderDetails: dislocationFractureRiderDetails,
    isDislocationApplied: defaultValues.get('ACCDislocationBenefit.isDislocationApplied'),
    dislocationDetails: dislocationDetails,
    isFractureApplied: defaultValues.get('ACCFractureBenefit.isFractureApplied'),
    fractureDetails: fractureDetails,

    //treatmentandrecovery

    unitsOfTreatmentSupportAndRecovery:
      Number(defaultValues.get('ACCBenefitStructure.unitsOfTreatmentSupportAndRecovery')) || 1,
    isCompanionNonLocalLodgingApplied: defaultValues.get('ACCBenefitStructure.isCompanionNonLocalLodgingApplied'),
    isMedicalEquipmentApplied: defaultValues.get('ACCBenefitStructure.isMedicalEquipmentApplied'),
    isMedicalSuppliesApplied: defaultValues.get('ACCBenefitStructure.isMedicalSuppliesApplied'),
    isMedicationApplied: defaultValues.get('ACCBenefitStructure.isMedicationApplied'),
    isNonLocalTransportationApplied: defaultValues.get('ACCBenefitStructure.isNonLocalTransportationApplied'),
    isPostAccidentCommonCarrierTransportationApplied: defaultValues.get(
      'ACCBenefitStructure.isPostAccidentCommonCarrierTransportationApplied'
    ),
    isProsthesisApplied: defaultValues.get('ACCBenefitStructure.isProsthesisApplied'),
    isResidenceAutomobileModificationApplied: defaultValues.get(
      'ACCBenefitStructure.isResidenceAutomobileModificationApplied'
    ),
    isTherapyApplied: defaultValues.get('ACCBenefitStructure.isTherapyApplied'),
    isSpinalManipulationServicesApplied: defaultValues.get('ACCBenefitStructure.isSpinalManipulationServicesApplied'),

    companionNonLocalLodgingDetails: {
      incurralPeriod: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.incurralPeriod'),
      distanceToHospitalMoreThanMilesCd: defaultValues.get(
        'ACCCompanionNonLocalLodgingBenefit.distanceToHospitalMoreThanMilesCd'
      ),
      benefitAmount: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCCompanionNonLocalLodgingBenefit', rootId, parentId),
    },

    medicalEquipmentDetails: {
      incurralPeriod: defaultValues.get('ACCMedicalEquipmentBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMedicalEquipmentBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMedicalEquipmentBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMedicalEquipmentBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMedicalEquipmentBenefit', rootId, parentId),
    },

    medicalSuppliesDetails: {
      incurralPeriod: defaultValues.get('ACCMedicalSuppliesBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMedicalSuppliesBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMedicalSuppliesBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMedicalSuppliesBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMedicalSuppliesBenefit', rootId, parentId),
    },

    medicationDetails: {
      incurralPeriod: defaultValues.get('ACCMedicationBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMedicationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMedicationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMedicationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMedicationBenefit', rootId, parentId),
    },

    nonLocalTransportationDetails: {
      incurralPeriod: defaultValues.get('ACCNonLocalTransportationBenefit.incurralPeriod'),
      travelDistanceMoreThan: defaultValues.get('ACCNonLocalTransportationBenefit.travelDistanceMoreThan'),
      benefitAmount: defaultValues.get('ACCNonLocalTransportationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCNonLocalTransportationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCNonLocalTransportationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCNonLocalTransportationBenefit', rootId, parentId),
    },

    postAccidentCommonCarrierTransportationDetails: {
      incurralPeriod: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.incurralPeriod'),
      travelDistanceMoreThan: defaultValues.get(
        'ACCPostAccidentCommonCarrierTransportationBenefit.travelDistanceMoreThan'
      ),
      benefitAmount: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCPostAccidentCommonCarrierTransportationBenefit', rootId, parentId),
    },

    // Prosthesis Details
    prosthesisDetails: {
      incurralPeriod: defaultValues.get('ACCProsthesisBenefit.incurralPeriod'),
      oneDeviceAmount: getMoneyObject(
        defaultValues.get('ACCProsthesisBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfTreatmentSupportAndRecovery')) || 1)
      ),
      twoOrMoreDevicesAmount: getMoneyObject(
        defaultValues.get('ACCProsthesisBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfTreatmentSupportAndRecovery')) || 1) *
          2
      ),
      maxBenefitNumber: defaultValues.get('ACCProsthesisBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCProsthesisBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCProsthesisBenefit', rootId, parentId),
    },

    // Residence Automobile Modification Details
    residenceAutomobileModificationDetails: {
      incurralPeriod: defaultValues.get('ACCResidenceAutomobileModificationBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCResidenceAutomobileModificationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCResidenceAutomobileModificationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCResidenceAutomobileModificationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCResidenceAutomobileModificationBenefit', rootId, parentId),
    },

    // Therapy Details
    therapyDetails: {
      incurralPeriod: defaultValues.get('ACCTherapyBenefit.incurralPeriod'),
      benefitAmount:
        situsStateCd === 'KS'
          ? defaultValues.get('ACCTherapyBenefit.benefitAmount')
          : defaultValues.get('ACCTherapyBenefit.standardBenefitAmount'),
      isAlternateTherapyApplied: defaultValues.get('ACCTherapyBenefit.isAlternateTherapyApplied'),
      maxBenefitNumber: defaultValues.get('ACCTherapyBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCTherapyBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCTherapyBenefit', rootId, parentId),
    },

    // spinalManipulationServicesDetails
    spinalManipulationServicesDetails: {
      incurralPeriod: defaultValues.get('ACCSpinalManipulationServicesBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCSpinalManipulationServicesBenefit.benefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCSpinalManipulationServicesBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCSpinalManipulationServicesBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCSpinalManipulationServicesBenefit', rootId, parentId),
    },

    unitsOfAccidentalDeathAndDismemberment:
      Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1,
    isAccidentalDeathApplied: defaultValues.get('ACCBenefitStructure.isAccidentalDeathApplied'),
    accidentalDeathDetails: {
      incurralPeriod: defaultValues.get('ACCAccidentalDeathBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCAccidentalDeathBenefit.standardBenefitAmount'),
      ...createEisTypeObject('ACCAccidentalDeathBenefit', rootId, parentId),
    },
    isAccidentalDeathCommonCarrierApplied: defaultValues.get(
      'ACCBenefitStructure.isAccidentalDeathCommonCarrierApplied'
    ),
    accidentalDeathCommonCarrierDetails: {
      incurralPeriod: defaultValues.get('ACCAccidentalDeathCommonCarrierBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCAccidentalDeathCommonCarrierBenefit.standardBenefitAmount'),
      ...createEisTypeObject('ACCAccidentalDeathCommonCarrierBenefit', rootId, parentId),
    },

    isAccidentalDismembermentApplied: defaultValues.get('ACCBenefitStructure.isAccidentalDismembermentApplied'),
    accidentalDismembermentDetails: {
      incurralPeriod: defaultValues.get('ACCAccidentalDismembermentBenefit.incurralPeriod'),
      bothArmsOrHandsLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),

      bothLegsOrFeetLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),
      oneHandOrArmAndOneLegOrFootLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),
      armOrHandLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          0.5
      ),
      legOrFootLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          0.5
      ),
      oneOrMoreFingersLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          (situsStateCd === 'NH' ? 0.05 : 0.1)
      ),
      oneOrMoreToesLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          (situsStateCd === 'NH' ? 0.05 : 0.1)
      ),
      maximumBenefitAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),
      maxBenefitNumber: defaultValues.get('ACCAccidentalDismembermentBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAccidentalDismembermentBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAccidentalDismembermentBenefit', rootId, parentId),
    },
    isFunctionalLossApplied: defaultValues.get('ACCBenefitStructure.isFunctionalLossApplied'),

    functionalLossDetails: {
      incurralPeriod: defaultValues.get('ACCFunctionalLossBenefit.incurralPeriod'),
      minimumDuration: defaultValues.get('ACCFunctionalLossBenefit.minimumDuration'),
      hearingLossAmount: getMoneyObject(
        defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          0.5
      ),
      sightLossAmount: getMoneyObject(
        defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          0.5
      ),
      speechLossAmount: getMoneyObject(
        defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),
      maximumBenefitAmount: getMoneyObject(
        defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),
      maxBenefitNumber: defaultValues.get('ACCFunctionalLossBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCFunctionalLossBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCFunctionalLossBenefit', rootId, parentId),
    },
    isAutomobileAccidentalDeathApplied: defaultValues.get('ACCBenefitStructure.isAutomobileAccidentalDeathApplied'),

    automobileAccidentalDeathDetails: automobileAccidentalDeathDetails,

    _type: 'ACCBenefitStructure',
    isPortabilityApplied: defaultValues.get('ACCBenefitStructure.isPortabilityApplied') ?? true,
    portabilityDetails: {
      ...createEisTypeObject('ACCPortability', rootId, parentId),
      limitPortedCovDurationInd: defaultValues.get('ACCPortability.limitPortedCovDurationInd'),
      covDuration: defaultValues.get('ACCPortability.covDuration'),
    },
  };
};

const createACCGAI7HealthScreeningServicesRiderDetails = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  situsStateCd?: string
) => {
  const hsbtypeObj = createEisTypeObject('ACCHealthScreeningServicesRider', rootId, parentId);
  return {
    healthScreeningType: defaultValues.get('ACCHealthScreeningServicesRider.healthScreeningType'),
    healthScreeningHashUnits:
      Number(defaultValues.get('ACCHealthScreeningServicesRider.healthScreeningHashUnits')) || 1,
    timePeriodCd: defaultValues.get('ACCHealthScreeningServicesRider.timePeriodCd'),
    benefitAmount: defaultValues.get('ACCHealthScreeningServicesRider.benefitAmount'),
    isPreventativeEnabled: defaultValues.get('ACCHealthScreeningServicesRider.isPreventativeEnabled'),
    // options: [
    //   {
    //     isOptionsAppliedUltrasoundForCancerDetection: defaultValues.get('AccHsbOptionUltrasoundForCancerDetection.isOptionApplied'),
    //     "assignedCategoryName": "CategoryFixed",
    //     "assignedSubCategoryName": "BASIC",
    //     "isOptionApplied": defaultValues.get('AccHsbOptionUltrasoundForCancerDetection.isOptionApplied'),
    //     ...createEisTypeObject('AccHsbOptionUltrasoundForCancerDetection', rootId, parentId),
    //   }
    // ],
    options: createHsbOptions(defaultValues, rootId, hsbtypeObj._key.id),
    optionCategories: [
      {
        categoryName: 'CategoryFixed',
        isApplied: true,
        ...createEisTypeObject('AccHsbCategoryFixed', rootId, parentId),
      },
    ],

    exclusions: createHsbExclusions(defaultValues, rootId, hsbtypeObj._key.id, situsStateCd),
    ...hsbtypeObj,
  };
};
const createHsbOptions = (defaultValues: ImmutableMap<string, any>, planRootId: string, hsbRiderId: string) => {
  return Object.keys(healthScreeningServicesRiderDetails).map((optionEntityName: string) => ({
    ...createEisTypeObject(optionEntityName, planRootId, hsbRiderId),
    assignedSubCategoryName: 'BASIC',
    assignedCategoryName: 'CategoryFixed',
    isOptionApplied: defaultValues.get(`${optionEntityName}.isOptionApplied`),
  }));
};

const createHsbExclusions = (
  defaultValues: ImmutableMap<string, any>,
  planRootId: string,
  hsbRiderId: string,
  situsStateCd: string | undefined
) => {
  return Object.keys(exclusionsMapping).map((optionEntityName: string) => {
    // Dynamically replace 'ACCExcl' with 'AccHsbExcl' in the optionEntityName
    //Ex:ACCExclAnyActOfWar AccHsbExclAnyActOfWar
    const updatedOptionEntityName = optionEntityName.replace(/^ACCExcl/, 'AccHsbExcl');

    return {
      ...createEisTypeObject(updatedOptionEntityName, planRootId, hsbRiderId),
      isExclusion: defaultValues.get(`${optionEntityName}.isExclusion`),
    };
  });
};

export const addNewPlanToQuote = (
  quote: Async<ImmutableMap<string, any>>,
  defaultValues: ImmutableMap<string, any> | undefined,
  plansWithCoverages: Async<RetreivedPlan[]>,
  planCd: string
) => {
  if (isLoaded(quote)) {
    let updatedPlans =
      (quote.Data?.getIn(parsePath('benefitPackagingDetail.plans')) as ImmutableList<any>) ?? ImmutableList();

    const coverageEffectiveDate = quote.Data?.getIn(parsePath('transactionDetails.txEffectiveDate')) ?? ImmutableList();

    const plan =
      isLoaded(plansWithCoverages) && Array.isArray(plansWithCoverages.Data)
        ? plansWithCoverages.Data?.find(p => p.planCode === planCd)
        : undefined;

    const situsStateCd = quote?.Data?.get('riskStateCd');

    const newPlanToAdd = createPlansObjectWithDefault(
      plan,
      quote.Data.getIn(parsePath('_key.rootId')) as string,
      quote.Data.getIn(parsePath('_key.parentId')) as string,
      coverageEffectiveDate as string,
      defaultValues ?? ImmutableMap(),
      situsStateCd
    );

    updatedPlans = updatedPlans.push(fromJS({...newPlanToAdd, planName: DEFAULT_INITIAL_PLAN_NAME}));

    return new Loaded(quote?.Data.setIn(parsePath('benefitPackagingDetail.plans'), updatedPlans));
  }

  return quote;
};
